import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  LinearProgress,
  Checkbox,
  Tooltip,
  IconButton,
} from '@mui/material';
import { MdCloudUpload, MdOutlineFileDownload } from 'react-icons/md';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useMain } from '../../hooks/useMain';

const REQUIRED_HEADERS = ['clientName', 'clientNumber', 'membershipName', 'serviceName', 'remainingCount'];

const norm = (s) => (s || '').toString().trim().toLowerCase();

// Matches header text loosely: "Client Number", "client_number", "clientNumber" all resolve
const HEADER_ALIASES = {
  clientName: ['clientname', 'client name'],
  clientNumber: ['clientnumber', 'client number', 'phone', 'mobile'],
  gender: ['gender'],
  membershipName: ['membershipname', 'membership name', 'membership'],
  serviceName: ['servicename', 'service name', 'service'],
  remainingCount: ['remainingcount', 'remaining count', 'count'],
  expireDate: ['expiredate', 'expire date', 'enddate', 'end date', 'validtill', 'valid till'],
};

const getField = (row, canonicalKey) => {
  const aliases = HEADER_ALIASES[canonicalKey] || [canonicalKey];
  const rowKeys = Object.keys(row);
  for (const alias of aliases) {
    const match = rowKeys.find((k) => norm(k).replace(/\s+/g, '') === alias.replace(/\s+/g, ''));
    if (match !== undefined) return row[match];
  }
  return undefined;
};

// Accepts either { data: [...] } or a raw array, matching the getAllServices API response shape
const extractServicesList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
};

const BulkUploadMembershipDialog = ({ services: servicesProp = [] }) => {
  const { activeBranchId, bulkUploadMembership, getAllServices, setShowMessage } = useMain();

  const [open, setOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState([]); // rows with resolved/unresolved service
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  // Services fetched live from the API, falling back to whatever was passed in via props
  const [services, setServices] = useState(servicesProp);
  const [servicesLoading, setServicesLoading] = useState(false);

  // Selection + bulk mapping controls
  const [selectedRows, setSelectedRows] = useState([]); // rowIndex[]
  const [bulkServiceId, setBulkServiceId] = useState('');

  const fetchServices = async () => {
    if (!getAllServices) return;
    setServicesLoading(true);
    try {
      const res = await getAllServices(`?branchId=${activeBranchId}`);
      const list = extractServicesList(res);
      if (list.length > 0) {
        setServices(list);
      } else if (servicesProp?.length) {
        setServices(servicesProp);
      }
    } catch (error) {
      setShowMessage?.({
        message: error?.message || 'Could not load services list',
        messageType: 'error',
      });
      if (servicesProp?.length) setServices(servicesProp);
    } finally {
      setServicesLoading(false);
    }
  };

  // Load services whenever the dialog opens, so the dropdown is always fresh
  useEffect(() => {
    if (open) {
      fetchServices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const serviceOptionsByName = React.useMemo(() => {
    const map = new Map();
    services.forEach((s) => map.set(norm(s.serviceName), s));
    return map;
  }, [services]);

  const reMatchRowsAgainstServices = (rows) =>
    rows.map((r) => {
      if (r.invalid) return r;
      // Keep an already-manually-mapped serviceId if it still exists in the fresh list
      if (r.serviceId) {
        const stillExists = services.find((s) => s._id === r.serviceId);
        if (stillExists) return { ...r, resolved: true };
      }
      const matchedService = serviceOptionsByName.get(norm(r.serviceName));
      return {
        ...r,
        serviceId: matchedService ? matchedService._id : r.serviceId || '',
        resolved: !!matchedService || !!r.serviceId,
      };
    });

  // Re-run auto matching whenever the services list refreshes (e.g. dialog re-opened)
  useEffect(() => {
    if (services.length && parsedRows.length) {
      setParsedRows((prev) => reMatchRowsAgainstServices(prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services]);

  const resetState = () => {
    setFileName('');
    setParsedRows([]);
    setParseError('');
    setSelectedRows([]);
    setBulkServiceId('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadSample = () => {
    const sampleData = [
      {
        clientName: 'Riya Sharma',
        clientNumber: '9876543210',
        gender: 'Female',
        membershipName: 'Top',
        serviceName: services?.[0]?.serviceName || 'Haircut',
        remainingCount: 10,
      },
      {
        clientName: 'Riya Sharma',
        clientNumber: '9876543210',
        gender: 'Female',
        membershipName: 'Top',
        serviceName: services?.[1]?.serviceName || 'Facial',
        remainingCount: 4,
      },
      {
        clientName: 'Aman Verma',
        clientNumber: '9876500000',
        gender: 'Male',
        membershipName: 'Top',
        serviceName: services?.[0]?.serviceName || 'Haircut',
        remainingCount: 2,
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    worksheet['!cols'] = [
      { wch: 18 }, // clientName
      { wch: 16 }, // clientNumber
      { wch: 10 }, // gender
      { wch: 16 }, // membershipName
      { wch: 18 }, // serviceName
      { wch: 14 }, // remainingCount
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sample');
    XLSX.writeFile(workbook, 'membership_bulk_upload_sample.xlsx');
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetState();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setSelectedRows([]);
    setBulkServiceId('');

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        if (!rows.length) {
          setParseError('The file has no data rows.');
          setParsedRows([]);
          return;
        }

        const firstRowKeys = Object.keys(rows[0]).map(norm);
        const missingHeaders = REQUIRED_HEADERS.filter((h) => {
          const aliases = HEADER_ALIASES[h] || [h];
          return !aliases.some((a) => firstRowKeys.some((k) => k.replace(/\s+/g, '') === a.replace(/\s+/g, '')));
        });
        if (missingHeaders.length > 0) {
          setParseError(`Missing required column(s): ${missingHeaders.join(', ')}`);
          setParsedRows([]);
          return;
        }

        const mapped = rows.map((row, idx) => {
          const clientName = (getField(row, 'clientName') || '').toString().trim();
          const clientNumber = (getField(row, 'clientNumber') || '').toString().trim();
          const gender = (getField(row, 'gender') || 'Both').toString().trim();
          const membershipName = (getField(row, 'membershipName') || '').toString().trim();
          const serviceName = (getField(row, 'serviceName') || '').toString().trim();
                const remainingCountRaw = getField(row, 'remainingCount');
          const remainingCount = Number(remainingCountRaw);
          const expireDateRaw = getField(row, 'expireDate');
          const expireDate = (expireDateRaw || '').toString().trim(); // expect YYYY-MM-DD; optional

          const matchedService = serviceOptionsByName.get(norm(serviceName));

          return {
            rowIndex: idx + 2, // header row + 1-index
            clientName,
            clientNumber,
            gender,
            membershipName,
            serviceName,
            remainingCount: isNaN(remainingCount) ? '' : remainingCount,
            expireDate,
            serviceId: matchedService ? matchedService._id : '',
            resolved: !!matchedService,
            invalid: !clientNumber || !membershipName || !serviceName || isNaN(remainingCount),
          };
        });

        setParsedRows(mapped);
      } catch (err) {
        setParseError('Could not read this file. Please upload a valid .xlsx or .csv file.');
        setParsedRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleServiceMap = (rowIndex, serviceId) => {
    setParsedRows((prev) =>
      prev.map((r) =>
        r.rowIndex === rowIndex
          ? {
              ...r,
              serviceId,
              resolved: !!serviceId,
              serviceName: services.find((s) => s._id === serviceId)?.serviceName || r.serviceName,
            }
          : r
      )
    );
  };

  // ---- Bulk selection helpers ----
  const selectableRows = parsedRows.filter((r) => !r.invalid);
  const allSelected = selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const someSelected = selectedRows.length > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(selectableRows.map((r) => r.rowIndex));
    }
  };

  const toggleSelectRow = (rowIndex) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex]
    );
  };

  const handleApplyBulkMapping = () => {
    if (!bulkServiceId) {
      setShowMessage?.({ message: 'Select a service to apply first', messageType: 'error' });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage?.({ message: 'Select at least one row to map', messageType: 'error' });
      return;
    }
    const service = services.find((s) => s._id === bulkServiceId);
    setParsedRows((prev) =>
      prev.map((r) =>
        selectedRows.includes(r.rowIndex)
          ? {
              ...r,
              serviceId: bulkServiceId,
              resolved: true,
              serviceName: service?.serviceName || r.serviceName,
            }
          : r
      )
    );
    setShowMessage?.({
      message: `Mapped ${selectedRows.length} row(s) to "${service?.serviceName || ''}"`,
      messageType: 'success',
    });
  };

  const handleMapAllUnresolved = () => {
    if (!bulkServiceId) {
      setShowMessage?.({ message: 'Select a service to apply first', messageType: 'error' });
      return;
    }
    const service = services.find((s) => s._id === bulkServiceId);
    const unresolvedRowIndexes = parsedRows.filter((r) => !r.invalid && !r.resolved).map((r) => r.rowIndex);
    if (unresolvedRowIndexes.length === 0) {
      setShowMessage?.({ message: 'No unmapped rows to update', messageType: 'error' });
      return;
    }
    setParsedRows((prev) =>
      prev.map((r) =>
        unresolvedRowIndexes.includes(r.rowIndex)
          ? {
              ...r,
              serviceId: bulkServiceId,
              resolved: true,
              serviceName: service?.serviceName || r.serviceName,
            }
          : r
      )
    );
    setShowMessage?.({
      message: `Mapped ${unresolvedRowIndexes.length} unmatched row(s) to "${service?.serviceName || ''}"`,
      messageType: 'success',
    });
  };

  const unresolvedCount = parsedRows.filter((r) => !r.invalid && !r.resolved).length;
  const invalidCount = parsedRows.filter((r) => r.invalid).length;
  const readyCount = parsedRows.filter((r) => !r.invalid && r.resolved).length;

  const uniqueMembershipCount = new Set(
    parsedRows.filter((r) => !r.invalid).map((r) => norm(r.membershipName))
  ).size;
  const uniqueClientCount = new Set(
    parsedRows.filter((r) => !r.invalid).map((r) => r.clientNumber)
  ).size;

  const handleSubmit = async () => {
    if (invalidCount > 0) {
      setShowMessage({
        message: `${invalidCount} row(s) are missing required fields — please fix the source file`,
        messageType: 'error',
      });
      return;
    }
    if (unresolvedCount > 0) {
      setShowMessage({
        message: `Please map all ${unresolvedCount} unmatched service name(s) before uploading`,
        messageType: 'error',
      });
      return;
    }
    if (selectedRows.length === 0) {
      setShowMessage({
        message: 'Please select at least one row to upload.',
        messageType: 'error',
      });
      return;
    }

    setSubmitting(true);
    try {
            const payload = {
        rows: parsedRows
          .filter((r) => selectedRows.includes(r.rowIndex))
          .map((r) => ({
          clientName: r.clientName,
          clientNumber: r.clientNumber,
          gender: r.gender,
          membershipName: r.membershipName,
          serviceId: r.serviceId,
          serviceName: r.serviceName,
          remainingCount: r.remainingCount,
          expireDate: r.expireDate || '',
        })),
      };

        const res = await bulkUploadMembership(activeBranchId, payload);    
        if (res?.statusCode === 200) {
        setShowMessage({
          message: res.message || 'Membership data uploaded successfully',
          messageType: 'success',
        });
        handleClose();
        // Caller (parent list) should refetch memberships after this succeeds.
      } else {
        setShowMessage({
          message: res?.message || 'Bulk upload failed',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage({
        message: error.message || 'Something went wrong during upload',
        messageType: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
            variant="contained"
            startIcon={<MdCloudUpload />}
            sx={{ background: '#6174DD', marginBottom: '12px' }}
            onClick={handleOpen}
        >
            Bulk Upload Membership
        </Button>
    </Box>

      <Dialog open={open} onClose={submitting ? undefined : handleClose} maxWidth={parsedRows.length > 0 ? 'lg' : 'xs'} fullWidth>
        <DialogTitle>Bulk Upload Membership Data <Tooltip
              title="Upload an excel/csv file with columns: clientName, clientNumber, gender, membershipName, serviceName, remainingCount. One row per client+service — repeat a client's row for each service they have remaining under a membership. A membership is created only once per unique name; matching clients get their own remaining counts. Not sure of the format? Download the sample file below."
              placement="right"
              arrow
            ><IconButton size="small" sx={{ p: 0 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
              </Tooltip>
              </DialogTitle>
        <DialogContent>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {/* <DialogContentText component="span" sx={{ mb: 0 }}>
              Upload an excel/csv file with columns: <b>clientName, clientNumber, gender, membershipName,
              serviceName, remainingCount</b>. One row per client+service — repeat a client&apos;s row for each
              service they have remaining under a membership. A membership is created only once per unique
              name; matching clients get their own remaining counts. Not sure of the format? Download the
              sample file below.
            </DialogContentText> 
            <Tooltip
              title="Upload an excel/csv file with columns: clientName, clientNumber, gender, membershipName, serviceName, remainingCount. One row per client+service — repeat a client's row for each service they have remaining under a membership. A membership is created only once per unique name; matching clients get their own remaining counts. Not sure of the format? Download the sample file below."
              placement="right"
              arrow
            >
              <IconButton size="small" sx={{ p: 0 }}>
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box> */}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Choose File
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<MdOutlineFileDownload />}
              onClick={handleDownloadSample}
              disabled={submitting}
            >
              Download Sample File
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
            {servicesLoading && <Typography variant="caption">Loading services…</Typography>}
          </Box>

          {submitting && <LinearProgress sx={{ mb: 2 }} />}

          {parseError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {parseError}
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`${parsedRows.length} rows total`} />
                <Chip label={`${uniqueClientCount} unique clients`} color="primary" variant="outlined" />
                <Chip
                  label={`${uniqueMembershipCount} unique membership(s)`}
                  color="primary"
                  variant="outlined"
                />
                <Chip label={`${readyCount} ready`} color="success" />
                {unresolvedCount > 0 && (
                  <Chip label={`${unresolvedCount} services need mapping`} color="warning" />
                )}
                {invalidCount > 0 && <Chip label={`${invalidCount} invalid rows`} color="error" />}
              </Box>

              {/* Bulk mapping toolbar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 1.5,
                  flexWrap: 'wrap',
                  p: 1.5,
                  border: '1px dashed #c5cae9',
                  borderRadius: '4px',
                  backgroundColor: '#f7f8fc',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Bulk map service:
                </Typography>
                <Select
                  size="small"
                  value={bulkServiceId}
                  displayEmpty
                  onChange={(e) => setBulkServiceId(e.target.value)}
                  sx={{ minWidth: 220 }}
                  disabled={servicesLoading || services.length === 0}
                >
                  <MenuItem value="">
                    <em>Select service...</em>
                  </MenuItem>
                  {services.map((s) => (
                    <MenuItem key={s._id} value={s._id}>
                      {s.serviceName}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleApplyBulkMapping}
                  disabled={selectedRows.length === 0 || !bulkServiceId}
                >
                  Apply to selected ({selectedRows.length})
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleMapAllUnresolved}
                  disabled={unresolvedCount === 0 || !bulkServiceId}
                >
                  Map all unmatched ({unresolvedCount})
                </Button>
                <Button size="small" onClick={fetchServices} disabled={servicesLoading}>
                  Refresh services
                </Button>
              </Box>

              <Box sx={{ maxHeight: '420px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleSelectAll}
                          disabled={selectableRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Row</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Client Number</TableCell>
                      <TableCell>Gender</TableCell>
                      <TableCell>Membership</TableCell>
                      <TableCell>Service (from file)</TableCell>
                      <TableCell>Mapped Service</TableCell>
                      <TableCell>Count</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedRows.map((r) => (
                      <TableRow
                        key={r.rowIndex}
                        selected={selectedRows.includes(r.rowIndex)}
                        sx={{
                          backgroundColor: r.invalid
                            ? '#ffebee'
                            : !r.resolved
                            ? '#fff8e1'
                            : 'inherit',
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRows.includes(r.rowIndex)}
                            onChange={() => toggleSelectRow(r.rowIndex)}
                            disabled={r.invalid}
                          />
                        </TableCell>
                        <TableCell>{r.rowIndex}</TableCell>
                        <TableCell>{r.clientName || '—'}</TableCell>
                        <TableCell>{r.clientNumber || '—'}</TableCell>
                        <TableCell>{r.gender}</TableCell>
                        <TableCell>{r.membershipName || '—'}</TableCell>
                        <TableCell>{r.serviceName || '—'}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            '—'
                          ) : (
                            <Select
                              size="small"
                              value={r.serviceId || ''}
                              displayEmpty
                              onChange={(e) => handleServiceMap(r.rowIndex, e.target.value)}
                              sx={{ minWidth: 180 }}
                              disabled={servicesLoading}
                            >
                              <MenuItem value="">
                                <em>Select service...</em>
                              </MenuItem>
                              {services.map((s) => (
                                <MenuItem key={s._id} value={s._id}>
                                  {s.serviceName}
                                </MenuItem>
                              ))}
                            </Select>
                          )}
                        </TableCell>
                        <TableCell>{r.remainingCount === '' ? '—' : r.remainingCount}</TableCell>
                        <TableCell>
                          {r.invalid ? (
                            <Chip size="small" label="Invalid" color="error" />
                          ) : r.resolved ? (
                            <Chip size="small" label="Ready" color="success" />
                          ) : (
                            <Chip size="small" label="Needs mapping" color="warning" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ background: '#6174DD' }}
            onClick={handleSubmit}
            disabled={submitting || parsedRows.length === 0 || selectedRows.length === 0 || !!parseError}
          >
            {submitting ? 'Uploading...' : `Upload ${selectedRows.length || ''} Membership Record(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkUploadMembershipDialog;